import React, { useState, useEffect } from "react";
import Cleave from "cleave.js/react";
import { createClient, getClientById, updateClient, getTipos } from "../api";
import { useContext } from "react";
import { AuthContext } from "../AuthContext";
import { 
  Factory, MapPinPlus, Headset, UserStar, ReceiptText, Crown,
  Save, BrushCleaning, SearchCheck, ArrowLeft 
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { validarCNPJ } from "../utils/validarCNPJ";
import { validarCEP } from "../utils/validarCEP";


export default function ClienteForm() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [clienteDuplicado, setClienteDuplicado] = useState(null);
  const [tipos, setTipos] = useState([]);

  const [form, setForm] = useState({
    cnpj: "",
    razao_social: "",
    nome_fantasia: "",
    inscricao_estadual: "",
    inscricao_municipal: "",
    cnae_principal: "",
    tipo_id: 1,
    situacao_cadastral: "Ativa",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    telefone: "",
    telefone_secundario: "",
    email: "",
    email_financeiro: "",
    representante: "",
    cargo: "",
    contato_telefone: "",
    contato_email: "",
    condicao_pagamento: "",
    limite_credito: "",
    forma_pagamento: "",
    observacoes: "",
    status: "ativo",
  });

  const [loadingCNPJ, setLoadingCNPJ] = useState(false);
  const [loadingCEP, setLoadingCEP] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingClient, setLoadingClient] = useState(id ? true : false);
  const [isEditMode, setIsEditMode] = useState(!!id);

  

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      const loadClient = async () => {
        try {
          const clientData = await getClientById(user.token, id);
          setForm(clientData);
        } catch (error) {
          console.error("Erro ao carregar cliente:", error);
          alert("Erro ao carregar dados do cliente.");
          navigate("/");
        } finally {
          setLoadingClient(false);
        }
      };
      loadClient();
    } else {
      setIsEditMode(false);
      setLoadingClient(false);
    }
    
    const fetchTipos = async () => {
      try {
        const data = await getTipos(user.token);
        setTipos(data);
      } catch (error) {
        console.error("Erro ao carregar tipos:", error);
      }
    };

    fetchTipos();
  }, [id, user.token, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // === BUSCA AUTOMÁTICA DE CNPJ ===
  const buscarCNPJ = async () => {
    const cnpj = form.cnpj.replace(/\D/g, "");

    if (!validarCNPJ(cnpj)) {
      alert("CNPJ inválido.");
      return;
    }

    setLoadingCNPJ(true);

    try {
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);

      if (!res.ok) throw new Error("Erro ao buscar CNPJ");

      const data = await res.json();

      setForm((prev) => ({
        ...prev,
        razao_social: data.razao_social || prev.razao_social,
        nome_fantasia: data.nome_fantasia || prev.nome_fantasia,
        logradouro: data.logradouro || prev.logradouro,
        numero: data.numero || prev.numero,
        bairro: data.bairro || prev.bairro,
        cidade: data.municipio || prev.cidade,
        uf: data.uf || prev.uf,
        cep: data.cep?.replace(/\D/g, "") || prev.cep,
        cnae_principal: data.cnae_fiscal_descricao || prev.cnae_principal,
        situacao_cadastral: data.descricao_situacao_cadastral || prev.situacao_cadastral || "Ativa",
      }));

    } catch {
      alert("Não foi possível consultar o CNPJ.");

    } finally {
      setLoadingCNPJ(false);

    }
  };

  // === BUSCA AUTOMÁTICA DE CEP ===
  const buscarCEP = async () => {
    const cep = form.cep.replace(/\D/g, "");

    if (!validarCEP(cep)) {
      alert("CEP inválido.");
      return;
    }

    setLoadingCEP(true);

    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);

      if (!res.ok) throw new Error("Erro ao buscar CEP");

      const data = await res.json();

      if (data.erro) throw new Error("CEP não encontrado.");

      setForm((prev) => ({
        ...prev,
        logradouro: data.logradouro || prev.logradouro,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        uf: data.uf || prev.uf,
      }));

    } catch {
      alert("Não foi possível consultar o CEP.");

    } finally {
      setLoadingCEP(false);

    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (isEditMode) {
        await updateClient(user.token, id, form);
        //alert("Cliente atualizado com sucesso!");
        navigate("/listclients");
      } else {
        //alert("Cliente cadastrado com sucesso!");
        const response = await createClient(user.token, form);
        //console.log("Resposta do cliente criado:", response);
        const novoClienteId = response.id;
        
        if (!novoClienteId) {
          throw new Error("Erro: ID do cliente não retornado");
        }
        limparFormulario();
        navigate(`/addcotacao?cliente=${novoClienteId}`);
      }
    } catch (error) {
        console.error("Erro:", error);

        if (error.code === "CNPJ_DUPLICADO" && error.cliente_id) {
          setClienteDuplicado({
            id: error.cliente_id,
            nome: error.razao_social,
          });
          setModalOpen(true);
          return;
        }

        alert(error.message || "Erro ao salvar cliente.");

    } finally {
      setSubmitting(false);
    }
  };

  if (loadingClient) {
    return (
      <div className="page-container">
        <div className="dashboard-container">
          <div className="user-card">
            <div className="spinner"></div>
            <p>Carregando cliente...</p>
          </div>
        </div>
      </div>
    );
  }

  const limparFormulario = () =>
    setForm({
      cnpj: "", razao_social: "", nome_fantasia: "", inscricao_estadual: "",
      inscricao_municipal: "", tipo_id: 1, cnae_principal: "", situacao_cadastral: "Ativa",
      cep: "", logradouro: "", numero: "", complemento: "", bairro: "",
      cidade: "", uf: "", telefone: "", telefone_secundario: "", email: "",
      email_financeiro: "", representante: "", cargo: "", contato_telefone: "",
      contato_email: "", condicao_pagamento: "", limite_credito: "", forma_pagamento: "",
      observacoes: "", status: "ativo",
    });

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-card">
          {/* HEADER */}
          <h3><Crown size={20} /> {isEditMode ? 'Editar Cliente' : 'Novo Cliente'}</h3>

          <form onSubmit={handleSubmit}>
            {/* ======= DADOS DA EMPRESA ======= */}
            <div className="section-card">
              <h4><Factory size={16} /> Dados da Empresa</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="labels">CNPJ *</label>
                  <div className="input-wrapper">
                    <Cleave
                      name="cnpj"
                      options={{ 
                        delimiters: [".", ".", "/", "-"], 
                        blocks: [2, 3, 3, 4, 2], 
                        numericOnly: true 
                      }}
                      value={form.cnpj}
                      onChange={handleChange}
                      placeholder="00.000.000/0000-00"
                      required
                      className="cleave-input"
                      inputMode="numeric"
                      pattern="\d{2}\.\d{3}\.\d{3}/\d{4}-\d{2}"
                    />
                    <button
                      type="button"
                      onClick={buscarCNPJ}
                      disabled={loadingCNPJ || !validarCNPJ(form.cnpj)}
                      className="search-btn"
                      title="Buscar CNPJ"
                    >
                      {loadingCNPJ ? (
                        <div className="spinner"></div>
                      ) : (
                        <SearchCheck size={16} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="labels">Situação Cadastral</label>
                  <select 
                    name="situacao_cadastral" 
                    value={form.situacao_cadastral} 
                    onChange={handleChange}
                  >
                    <option value="Ativa">Ativa</option>
                    <option value="Inapta">Inapta</option>
                    <option value="Suspensa">Suspensa</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="labels">Razão Social *</label>
                  <input
                    name="razao_social"
                    value={form.razao_social}
                    onChange={handleChange}
                    placeholder="Razão Social Completa"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="labels">Nome Fantasia *</label>
                  <input
                    name="nome_fantasia"
                    value={form.nome_fantasia}
                    onChange={handleChange}
                    placeholder="Nome Fantasia"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="labels">Inscrição Estadual</label>
                  <input
                    name="inscricao_estadual"
                    value={form.inscricao_estadual}
                    onChange={handleChange}
                    placeholder="Inscrição Estadual"
                    inputMode="numeric"
                  />
                </div>
                <div className="form-group">
                  <div className="form-group">
                    <label className="labels">Tipo *</label>
                    <select
                      name="tipo_id"
                      value={form.tipo_id}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          tipo_id: parseInt(e.target.value),
                        }))
                      }
                      required
                    >
                      <option value="">Selecione...</option>
                      {tipos.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.descricao}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* ======= ENDEREÇO ======= */}
            <div className="section-card">
              <h4><MapPinPlus size={16} /> Endereço</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="labels">CEP *</label>
                  <div className="input-wrapper">
                    <Cleave
                      name="cep"
                      options={{ 
                        delimiters: ["-"], 
                        blocks: [5, 3], 
                        numericOnly: true 
                      }}
                      value={form.cep}
                      onChange={handleChange}
                      placeholder="00000-000"
                      className="cleave-input"
                      inputMode="numeric"
                      pattern="\d{5}-\d{3}"
                      required
                    />
                    <button
                      type="button"
                      onClick={buscarCEP}
                      disabled={loadingCEP || !validarCEP(form.cep)}
                      className="search-btn"
                      title="Buscar CEP"
                    >
                      {loadingCEP ? (
                        <div className="spinner"></div>
                      ) : (
                        <SearchCheck size={16} />
                      )}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="labels">UF *</label>
                  <input
                    name="uf"
                    value={form.uf}
                    onChange={handleChange}
                    placeholder="UF"
                    maxLength={2}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="labels">Logradouro *</label>
                  <input
                    name="logradouro"
                    value={form.logradouro}
                    onChange={handleChange}
                    placeholder="Rua, Avenida, etc."
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="labels">Número *</label>
                  <input
                    name="numero"
                    value={form.numero}
                    onChange={handleChange}
                    placeholder="Número"
                    inputMode="numeric"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="labels">Complemento</label>
                  <input
                    name="complemento"
                    value={form.complemento}
                    onChange={handleChange}
                    placeholder="Complemento"
                  />
                </div>
                <div className="form-group">
                  <label className="labels">Bairro *</label>
                  <input
                    name="bairro"
                    value={form.bairro}
                    onChange={handleChange}
                    placeholder="Bairro"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="labels">Cidade *</label>
                <input
                  name="cidade"
                  value={form.cidade}
                  onChange={handleChange}
                  placeholder="Cidade"
                  required
                />
              </div>
            </div>

            {/* ======= CONTATOS ======= */}
            <div className="section-card">
              <h4><Headset size={16} /> Contatos</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="labels">Telefone Principal *</label>
                  <Cleave
                    name="telefone"
                    options={{ delimiters: ["(", ") ", "-"], blocks: [0, 2, 5, 4], numericOnly: true }}
                    value={form.telefone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    inputMode="numeric"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="labels">Telefone Secundário</label>
                  <Cleave
                    name="telefone_secundario"
                    options={{ delimiters: ["(", ") ", "-"], blocks: [0, 2, 5, 4], numericOnly: true }}
                    value={form.telefone_secundario}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="labels">E-mail Principal *</label>
                    <small style={{ 
                        color: 'var(--text_color-secondary)', 
                        display: 'block', 
                        fontSize: '0.8rem',
                        marginTop: '2px',
                        fontStyle: 'italic'
                      }}>
                        Email para enviar a cotação / pedido
                    </small>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="contato@empresa.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="labels">E-mail Financeiro</label>
                  <small style={{ 
                        color: 'var(--text_color-secondary)', 
                        display: 'block', 
                        fontSize: '0.8rem',
                        marginTop: '2px',
                        fontStyle: 'italic'
                      }}>
                        Email para contato com setor financeiro
                    </small>
                  <input
                    name="email_financeiro"
                    type="email"
                    value={form.email_financeiro}
                    onChange={handleChange}
                    placeholder="financeiro@empresa.com"
                  />
                </div>
              </div>
            </div>

            {/* ======= REPRESENTANTE ======= */}
            <div className="section-card">
              <h4><UserStar size={16} />Contato Feira</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="labels">Nome do Contato *</label>
                  <input
                    name="representante"
                    value={form.representante}
                    onChange={handleChange}
                    placeholder="Nome do Contato na Feira"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="labels">Cargo / Função</label>
                  <input
                    name="cargo"
                    value={form.cargo}
                    onChange={handleChange}
                    placeholder="Cargo do Contato"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="labels">Telefone / WhatsApp</label>
                  <Cleave
                    name="contato_telefone"
                    options={{ delimiters: ["(", ") ", "-"], blocks: [0, 2, 5, 4], numericOnly: true }}
                    value={form.contato_telefone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    inputMode="numeric"
                  />
                </div>
                <div className="form-group">
                  <label className="labels">E-mail do Contato</label>
                  <input
                    name="contato_email"
                    type="email"
                    value={form.contato_email}
                    onChange={handleChange}
                    placeholder="contato@empresa.com"
                  />
                </div>
              </div>
            </div>

            {/* ======= DADOS COMERCIAIS ======= */}
            <div className="section-card">
              <h4><ReceiptText size={16} /> Dados Comerciais</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="labels">Condição de Pagamento</label>
                  <input
                    name="condicao_pagamento"
                    value={form.condicao_pagamento}
                    onChange={handleChange}
                    placeholder="30 dias, à vista, etc."
                  />
                </div>
                <div className="form-group">
                  <label className="labels">Forma de Pagamento</label>
                  <input
                    name="forma_pagamento"
                    value={form.forma_pagamento}
                    onChange={handleChange}
                    placeholder="Boleto, Transferência, etc."
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="labels">Limite de Crédito</label>
                <input
                  name="limite_credito"
                  type="number"
                  value={form.limite_credito}
                  onChange={handleChange}
                  placeholder="0.00"
                  inputMode="numeric"
                />
              </div>

              <div className="form-group">
                <label className="labels">Observações Comerciais</label>
                <textarea
                  name="observacoes"
                  value={form.observacoes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Observações adicionais..."
                />
              </div>
            </div>

            {/* ======= BOTÕES ======= */}
            <div className="form-group">
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary"
              >
                <Save size={18} style={{ marginRight: '0.5rem' }} />
                {submitting ? (
                  <>
                    <span className="spinner" /> {isEditMode ? "Atualizando..." : "Salvando..."}
                  </>
                ) : (
                  ` ${isEditMode ? "Atualizar" : "Salvar"} Cliente`
                )}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={isEditMode ? () => navigate(-1) : limparFormulario}
              >
                {isEditMode ? (
                  <>
                    <ArrowLeft size={18} style={{ marginRight: '0.5rem' }} /> Voltar
                  </>
                ) : (
                  <>
                    <BrushCleaning size={18} style={{ marginRight: '0.5rem' }} /> Limpar
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
          <ConfirmModal
            isOpen={modalOpen}
            title="CNPJ já cadastrado"
            message={
              clienteDuplicado
                ? `Este CNPJ já pertence a "${clienteDuplicado.nome}". O que deseja fazer?`
                : ""
            }
            onClose={() => setModalOpen(false)}
            actions={[
              {
                label: "Ver Cliente",
                color: "#6c757d",
                onClick: () => {
                  navigate(`/listclients/${clienteDuplicado.id}`);
                },
              },
              {
                label: "Ver Cotações",
                color: "#17a2b8",
                onClick: () => {
                  navigate(`/listcotacoes?cliente=${clienteDuplicado.id}`);
                },
              },
              {
                label: "Nova Cotação",
                color: "#28a745",
                onClick: () => {
                  navigate(`/addcotacao?cliente=${clienteDuplicado.id}`);
                },
              },
            ]}
          />
    </div>
  );
}
